import styled, { keyframes } from 'styled-components';

import { Root as ButtonRoot } from '../../../../components/Button/Button.styles';
import { Text } from '../../../../components/Text/Text';
import { ANIM, ART_STYLE, COLORS, MOBILE, REDUCED_MOTION, SPACE } from '../../../../theme/tokens';

/** A tile row's height basis (desktop / phone): each tile's width is its ratio times this, so a row's tiles stand the
 *  same height and grow together to fill it. */
const TILE_ROW_PX = 150;
const TILE_ROW_MOBILE_PX = 96;
/** How far past its basis a tile may grow (a lone wide tile on the last row stays a sensible size). */
const TILE_GROW_CAP = 1.8;

/* The look's name, status and action as the card's header, like a style's: edge to edge (the negative margins undo the
 * Panel's 24px / 26px padding, 20px / 16px on mobile, off the scale on purpose), a shade lighter than the card. */
export const Header = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: ${SPACE[16]};
  margin: -24px -26px ${SPACE[24]};
  padding: ${SPACE[20]} 26px;
  border-radius: 20px 20px 0 0;
  border-bottom: 1px solid ${COLORS.hairline};
  background: ${COLORS.surfaceFainter};

  ${MOBILE} {
    flex-direction: column;
    align-items: stretch;
    gap: ${SPACE[12]};
    margin: -20px -16px ${SPACE[20]};
    padding: ${SPACE[16]} 16px;
  }
`;

export const NameBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACE[4]};
  min-width: 0;
  flex: 1 1 auto;
`;

export const EyebrowRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${SPACE[8]};
`;

export const Eyebrow = styled(Text)`
  font-size: 12px;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${COLORS.textFaint};
`;

/** "Original": the look the site launched with. */
export const Tag = styled.span`
  display: inline-flex;
  align-items: center;
  padding: ${SPACE[2]} ${SPACE[8]};
  border-radius: 999px;
  border: 1px solid ${COLORS.hairline};
  color: ${COLORS.textMuted};
  font-size: 11px;
  font-weight: 600;
  line-height: 1.4;
`;

export const MatureTag = styled(Tag)`
  color: ${COLORS.danger};
  font-weight: 700;
`;

export const Name = styled(Text)`
  margin: 0;
  overflow-wrap: anywhere;
  color: ${COLORS.heading};
  font-size: 24px;
  font-weight: 600;
  line-height: 1.25;

  ${MOBILE} {
    font-size: 20px;
  }
`;

export const Status = styled(Text)`
  margin: 0;
  font-size: 14px;
  line-height: 1.45;
  color: ${COLORS.textMuted};
`;

export const Actions = styled.div`
  flex: none;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${SPACE[8]};
`;

const breathe = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.45; }
`;

/** The look the website shows: the strip's Primary badge, at the header's scale, in the theme's primary orange. */
export const PrimaryBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${SPACE[8]};
  padding: ${SPACE[4]} ${SPACE[12]};
  border-radius: 999px;
  border: 1px solid ${COLORS.hairline};
  background: ${COLORS.surfaceCard};
  color: ${COLORS.ctaSolid};
  font-size: 13px;
  font-weight: 600;
  line-height: 1.4;
`;

export const PrimaryDot = styled.span`
  width: 7px;
  height: 7px;
  border-radius: 999px;
  background: currentColor;
  animation: ${breathe} ${ANIM.LIVE_DOT_PULSE_MS}ms ease-in-out infinite;

  ${REDUCED_MOTION} {
    animation: none;
  }
`;

/** "Preview on your site": the outline button's look, as a link that opens the site in a new tab. */
export const PreviewLink = styled(ButtonRoot)`
  text-decoration: none;
`;

export const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACE[16]};
`;

export const SectionHead = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACE[4]};
`;

export const SectionTitle = styled(Text)`
  font-size: 15px;
  font-weight: 600;
  color: ${COLORS.text};
`;

export const SectionNote = styled(Text)`
  font-size: 13px;
  line-height: 1.45;
  color: ${COLORS.textMuted};
`;

/** The draw's progress while this look is drawn. */
export const Progress = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACE[8]};
`;

export const ProgressCopy = styled(Text)`
  font-size: 13px;
  color: ${COLORS.textMuted};
`;

/** The tiles, a justified row each: a tile's width follows its ratio, so every tile in a row stands the same height. */
export const Tiles = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${SPACE[12]};
`;

export const Tile = styled.figure<{ $aspect: number }>`
  flex: ${({ $aspect }) => $aspect} 1 ${({ $aspect }) => $aspect * TILE_ROW_PX}px;
  max-width: ${({ $aspect }) => $aspect * TILE_ROW_PX * TILE_GROW_CAP}px;
  min-width: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: ${SPACE[8]};

  ${MOBILE} {
    flex-basis: ${({ $aspect }) => $aspect * TILE_ROW_MOBILE_PX}px;
    max-width: 100%;
  }
`;

export const Media = styled.div<{ $aspect: number }>`
  position: relative;
  aspect-ratio: ${({ $aspect }) => $aspect};
  border-radius: 12px;
  overflow: hidden;
  background: ${COLORS.surfaceInset};
  border: 1px solid ${COLORS.hairline};
`;

export const Img = styled.img<{ $loaded: boolean }>`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  opacity: ${({ $loaded }) => ($loaded ? 1 : 0)};
  transition: opacity ${ART_STYLE.SAMPLE_FADE_MS}ms ${ANIM.EASE};

  ${REDUCED_MOTION} {
    transition: none;
  }
`;

export const Placeholder = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${SPACE[4]};
  padding: ${SPACE[8]};
  color: ${COLORS.textFaint};
  text-align: center;
`;

export const PlaceholderText = styled(Text)`
  font-size: 11.5px;
`;

export const Overlay = styled.div`
  position: absolute;
  inset: 0;
`;

export const Backdrop = styled.div`
  position: absolute;
  inset: 0;
  background: ${COLORS.surfaceCard};
`;

export const Foot = styled.figcaption`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${SPACE[4]} ${SPACE[8]};
  min-width: 0;
`;

export const TileName = styled(Text)`
  min-width: 0;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  font-size: 13px;
  color: ${COLORS.text};
`;

export const TileMeta = styled(Text)`
  font-size: 11.5px;
  color: ${COLORS.textFaint};
`;

/** No art in the look yet: what Generate does, and the trigger. */
export const Empty = styled.div`
  display: flex;
  align-items: center;
  gap: ${SPACE[16]};
  padding: ${SPACE[16]};
  border-radius: 14px;
  border: 1px dashed ${COLORS.hoverBorder};
  background: ${COLORS.surfaceFainter};

  ${MOBILE} {
    flex-direction: column;
    align-items: flex-start;
  }
`;

export const EmptyCopy = styled.div`
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: ${SPACE[4]};
`;

export const EmptyTitle = styled(Text)`
  font-size: 15px;
  font-weight: 600;
  color: ${COLORS.text};
`;

export const EmptyText = styled(Text)`
  font-size: 13px;
  line-height: 1.45;
  color: ${COLORS.textMuted};
`;

/** A screenshot of a site of the brand's design in the look, before the brand has art in it. */
export const Shot = styled.figure`
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: ${SPACE[8]};
`;

export const ShotFrame = styled.div`
  position: relative;
  max-height: 360px;
  overflow: hidden;
  border-radius: 12px;
  border: 1px solid ${COLORS.hairline};
  background: ${COLORS.surfaceInset};
`;

export const ShotImg = styled.img`
  display: block;
  width: 100%;
  height: auto;
`;

export const ShotCaption = styled(Text)`
  font-size: 12px;
  color: ${COLORS.textFaint};
`;
